import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Badge from './Badge';
import './TaskCard.css';

interface TaskCardProps {
  task: {
    id: string;
    title: string;
    description: string | null;
    status: string;
    owner: string | null;
    due_date: string | null;
    task_type: string | null;
    person_id: string | null;
    person_name?: string | null;
    company_id: string | null;
    company_name?: string | null;
    project_id: string | null;
    project_name?: string | null;
    github_issue_url: string;
  };
  onStatusChange: (taskId: string, newStatus: string) => void;
  onEdit: (taskId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({ task, onStatusChange, onEdit }) => {
  const [expanded, setExpanded] = useState(false);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.stopPropagation();
    onStatusChange(task.id, e.target.value);
  };

  return (
    <div className="task-card">
      <div className="task-card-header">
        <button
          className="task-card-title"
          onClick={() => onEdit(task.id)}
          type="button"
        >
          {task.title}
        </button>
        <div className="task-card-actions">
          <a
            href={task.github_issue_url}
            target="_blank"
            rel="noopener noreferrer"
            className="task-card-github-link"
            title="Open in GitHub"
            onClick={(e) => e.stopPropagation()}
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z" />
            </svg>
          </a>
          <button
            className="task-card-expand"
            onClick={() => setExpanded(!expanded)}
            type="button"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="currentColor"
              style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}
            >
              <path d="M4.427 7.427l3.396 3.396a.25.25 0 00.354 0l3.396-3.396A.25.25 0 0011.396 7H4.604a.25.25 0 00-.177.427z" />
            </svg>
          </button>
        </div>
      </div>

      <div className="task-card-meta">
        {task.owner && (
          <Badge variant="default">
            👤 {task.owner}
          </Badge>
        )}
        {task.due_date && (
          <Badge variant="warning">
            📅 {formatDate(task.due_date)}
          </Badge>
        )}
        {task.task_type && (
          <Badge variant="default">
            {task.task_type}
          </Badge>
        )}
      </div>

      {expanded && (
        <div className="task-card-expanded">
          {task.description && (
            <p className="task-card-description">{task.description}</p>
          )}
          <div className="task-card-links">
            {(task.person_id || task.person_name) && (
              <span className="task-card-link">
                👤 Person:{' '}
                {task.person_id ? (
                  <Link to={`/people/${task.person_id}`} onClick={(e) => e.stopPropagation()}>
                    {task.person_name ?? task.person_id}
                  </Link>
                ) : (
                  <span>{task.person_name}</span>
                )}
              </span>
            )}
            {(task.company_id || task.company_name) && (
              <span className="task-card-link">
                🏢 Company:{' '}
                {task.company_id ? (
                  <Link to={`/companies/${task.company_id}`} onClick={(e) => e.stopPropagation()}>
                    {task.company_name ?? task.company_id}
                  </Link>
                ) : (
                  <span>{task.company_name}</span>
                )}
              </span>
            )}
            {(task.project_id || task.project_name) && (
              <span className="task-card-link">
                📁 Project:{' '}
                {task.project_id ? (
                  <Link to={`/projects/${task.project_id}`} onClick={(e) => e.stopPropagation()}>
                    {task.project_name ?? task.project_id}
                  </Link>
                ) : (
                  <span>{task.project_name}</span>
                )}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="task-card-footer">
        <select
          className="task-card-status-select"
          value={task.status}
          onChange={handleStatusChange}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="To do">To do</option>
          <option value="Doing">Doing</option>
          <option value="Done">Done</option>
        </select>
      </div>
    </div>
  );
};

export default TaskCard;
